import React, { useState, useRef, useEffect } from 'react';
import AddTesSectionDialog from './AddTesSectionDialog';
import { TesSectionApi } from '../../services/estimateService';
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputBase,
  Menu,
  MenuItem,
} from '@mui/material';
import { MoreVert, Undo, Redo } from '@mui/icons-material';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import { Estimate } from '../../pages/EstimatesPage';
import { getTesSections } from '../../services/estimateService';
import { compareSectionNr, findBestSection } from '../../utils/sectionUtils';
import { deleteTesSection } from '../../services/estimateService';

interface TesSection {
  id: number;
  nr: string; // L.p
  range: string; // Pozycje kosztorysowe (np. 1-214)
  name: string; // Nazwa działu
  total: number; // RAZEM
}

const sampleNames = [
  'ROBOTY ZIEMNE',
  'FUNDAMENTY',
  'KONSTRUKCJA ŻELBETOWA',
  'ŚCIANY I KOMINY',
  'STROPY',
  'DACH',
  'ROBOTY WYKOŃCZENIOWE WEWNĘTRZNE',
  'ROBOTY WYKOŃCZENIOWE ZEWNĘTRZNE',
  'INSTALACJE SANITARNE',
  'INSTALACJE ELEKTRYCZNE',
];

export const generateSampleTesSections = (): TesSection[] => {
  return Array.from({ length: 15 }).map((_, i) => {
    const startPos = i === 0 ? 1 : i * 20 + 1;
    const endPos = startPos + 19;
    return {
      id: i + 1,
      nr: (i + 1).toString(),
      range: `${startPos}-${endPos}`,
      name: sampleNames[i % sampleNames.length],
      total: Math.round(Math.random() * 1500000 + 50000),
    };
  });
};

interface TesTabProps {
  estimate: Estimate;
}

const TesTab: React.FC<TesTabProps> = ({ estimate }) => {
  const { data, setSectionData } = useEstimateData();
  const initial = (data?.tesRows || []) as TesSection[];
  const sortByNr = (arr:TesSection[])=>[...arr].sort((a,b)=>compareSectionNr(a.nr,b.nr));
  const { state: rows, setState: setRows, undo, redo, canUndo, canRedo } = useUndoRedo<TesSection[]>(sortByNr(initial));

  // Aktualizuj, gdy tesRows w context-cie zmieni się (np. dodane z innej karty)
  useEffect(() => {
    setRows(() => sortByNr((data?.tesRows || []) as TesSection[]));
  }, [data?.tesRows, setRows]);

  // Jeśli brak sekcji w context-cie, pobierz z backendu
  useEffect(() => {
    const fetchIfEmpty = async () => {
      if ((!data?.tesRows || data.tesRows.length===0) && estimate.id) {
        try {
          const list = await getTesSections(estimate.id);
          setSectionData('tesRows', list);
        } catch (err) { console.error('[TesTab] fetch error', err); }
      }
    };
    fetchIfEmpty();
  }, [data?.tesRows, estimate.id, setSectionData]);

  // brak zaznaczenia komórek – edycja wyłączona
  const [search, setSearch] = useState('');
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);

  // Dialog Add Section
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSection, setEditSection] = useState<TesSection|null>(null);

  // otwarcie dialogu (dodaj / edytuj)
  const openEditSection = (row?:TesSection) => {
    setEditSection(row || null);
    setDialogOpen(true);
  };

  const handleDialogSaved = (sec: TesSectionApi) => {
    // zaktualizuj context i lokalny stan
    const prev = (data?.tesRows || []) as TesSection[];
    const exists = prev.findIndex(s=>s.id===sec.id);
    let updated: TesSection[];
    if(exists>-1){
      updated = prev.map(s=> s.id===sec.id ? sec : s);
    } else {
      updated = [...prev, sec];
    }
    const sorted = sortByNr(updated as TesSection[]);
    setSectionData('tesRows', sorted);
    setRows(() => sorted);
  };

  // Shortcut D
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'd' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const tag = (e.target as HTMLElement).tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
        e.preventDefault();
        openEditSection();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // Ustaw fokus na tabelę po zamontowaniu, aby strzałki działały od razu
  useEffect(()=>{
    tableContainerRef.current?.focus();
  },[]);

  // Menu kontekstowe (usuń)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, rowId: number) => {
    setMenuAnchorEl(e.currentTarget);
    setSelectedRowId(rowId);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);
  const handleDeleteRow = async () => {
    if(selectedRowId===null) return;
    try {
      await deleteTesSection(estimate.id, selectedRowId);
      const updated = (data?.tesRows || []).filter(r=>r.id!==selectedRowId);
      setSectionData('tesRows', updated);
      setRows(()=>sortByNr(updated as TesSection[]));
    } catch(err){ console.error('[TesTab] delete error', err); }
    handleMenuClose();
  };

  // Pomocnicza funkcja licząca cenę jedn. pozycji na podstawie RMS i narzutów z nagłówka
  const computeUnitPrice = React.useCallback((pos:any):number=>{
    if(!pos) return 0;
    if(pos.positionType==='simplified' || pos.position_type==='U'){
      return (pos.unitPrice || pos.unit_price || 0);
    }
    const children = (pos.children || pos.rms || []) as any[];
    const sumR = children.filter((c:any)=>c.type==='R' || c.rms_type==='R').reduce((s:number,c:any)=> s + (parseFloat(c.norm)||0) * (parseFloat(c.rmsPrice||c.rms_price)||0), 0);
    const sumM = children.filter((c:any)=>c.type==='M' || c.rms_type==='M').reduce((s:number,c:any)=> s + (parseFloat(c.norm)||0) * (parseFloat(c.rmsPrice||c.rms_price)||0), 0);
    const sumS = children.filter((c:any)=>c.type==='S' || c.rms_type==='S').reduce((s:number,c:any)=> s + (parseFloat(c.norm)||0) * (parseFloat(c.rmsPrice||c.rms_price)||0), 0);
    const kp = (estimate as any).kp ?? (estimate as any).markup_kp ?? 0;
    const kz = (estimate as any).kz ?? (estimate as any).markup_kz ?? 0;
    const zz = (estimate as any).z  ?? (estimate as any).markup_z  ?? 0;
    const kpUnit = (kp/100)*sumR;
    const kzUnit = (kz/100)*sumM;
    const zUnit  = (zz/100)*(sumR+sumM+sumS+kpUnit+kzUnit);
    return sumR+sumM+sumS+kpUnit+kzUnit+zUnit;
  }, [estimate]);

  // Pozycje kosztorysu (z contextu DetailedEstimateTab)
  const detailedRows = (data?.detailedRows || []) as any[];

  // Uzupełnij totals w TES na podstawie zakresów i pozycji – licz na bieżąco (bez zapisywania w stanie)
  const enhancedRows = React.useMemo(()=>{
    if(!rows || rows.length===0) return [];
    const parseRange = (rng:string) => {
      const m = (rng||'').match(/(\d+)\s*-\s*(\d+)/);
      if(!m) return null;
      const a = parseInt(m[1],10), b = parseInt(m[2],10);
      if(!Number.isFinite(a)||!Number.isFinite(b)) return null;
      return { from: Math.min(a,b), to: Math.max(a,b) };
    };
    return rows.map((r:TesSection)=>{
      // STRICT: dział = dokładnie pozycje z listy Szczegółowego/Uproszczonego od X do Y (bez żadnych „najlepszych dopasowań”)
      const range = parseRange(r.range);
      if(!range) return r;
      const inSection = detailedRows.filter((p:any)=>{
        const seq = p?.nr ? parseInt(p.nr,10) : (p?.seq_no || 0);
        return Number.isFinite(seq) && seq>=range.from && seq<=range.to;
      });
      const total = inSection.reduce((s:number, p:any)=>{
        const qty = parseFloat(p.quantity)||0;
        const value = computeUnitPrice(p) * qty;
        return s + value;
      }, 0);
      return {...r, total};
    });
  }, [rows, detailedRows, computeUnitPrice]);

  // Filtr
  const filteredRows = enhancedRows.filter(r =>
    r.range.includes(search) || r.name.toLowerCase().includes(search.toLowerCase())
  );

  // Keyboard navigation & editing
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // obsługuj klawisze tylko, gdy fokus jest w tabeli
    if(!tableContainerRef.current?.contains(e.target as Node)) return;
    const tag = (e.target as HTMLElement).tagName;
    if(tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
    // tylko nawigacja strzałkami między wierszami

    // Jeśli brak zaznaczenia i naciśnięto strzałkę – zaznacz pierwszy wiersz
    if(!selectedRowId && (e.key==='ArrowDown' || e.key==='ArrowUp')){
      if(filteredRows.length>0){
        setSelectedRowId(filteredRows[0].id);
        scrollRowIntoView(filteredRows[0].id);
      }
      return;
    }

    // Rzędy
    const rowsEls = Array.from(document.querySelectorAll('[data-rowid]')) as HTMLElement[];
    const currentIndex = rowsEls.findIndex(el => el.dataset.rowid === `row-${selectedRowId}`);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentIndex < rowsEls.length - 1) {
        const next = rowsEls[currentIndex + 1];
        const id = parseInt(next.dataset.rowid!.split('-')[1]);
        setSelectedRowId(id);
        scrollRowIntoView(id);
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentIndex > 0) {
        const prev = rowsEls[currentIndex - 1];
        const id = parseInt(prev.dataset.rowid!.split('-')[1]);
        setSelectedRowId(id);
        scrollRowIntoView(id);
      }
    }
    if ((e.key === 'Enter' || e.key === 'F2') && selectedRowId !== null) {
      // setSelectedCell({ id: selectedRowId, field: 'range' }); // Removed per-cell editing
    }
    // if (e.key.length === 1 && selectedRowId !== null) { // Removed per-cell editing
    //   setSelectedCell({ id: selectedRowId, field: 'range' });
    //   setEditingCell({ id: selectedRowId, field: 'range', value: e.key });
    // }
  };

  const scrollRowIntoView = (id: number) => {
    const rowEl = document.querySelector(`[data-rowid="row-${id}"]`) as HTMLElement | null;
    const container = tableContainerRef.current;
    if (rowEl && container) {
      const rowBottom = rowEl.offsetTop + rowEl.offsetHeight;
      if (rowBottom > container.scrollTop + container.clientHeight) {
        container.scrollTop = rowBottom - container.clientHeight;
      }
      if (rowEl.offsetTop < container.scrollTop) {
        const headerH = (container.querySelector('thead') as HTMLElement)?.offsetHeight || 0;
        container.scrollTop = rowEl.offsetTop - headerH;
      }
    }
  };

  // global shortcut for undo/redo
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') { e.preventDefault(); canUndo && undo(); }
      if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') || ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'z')) { e.preventDefault(); canRedo && redo(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [canUndo, canRedo, undo, redo]);

  useEffect(()=>{ setSectionData('tesRows', rows); }, [rows, setSectionData]);

  return (
    <Box sx={{ height: '100%', outline: 'none' }} tabIndex={0}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip title="Cofnij (⌘Z)"><span><IconButton size="small" onClick={undo} disabled={!canUndo}><Undo fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Ponów (⌘Y)"><span><IconButton size="small" onClick={redo} disabled={!canRedo}><Redo fontSize="small" /></IconButton></span></Tooltip>
        <Tooltip title="Dodaj dział"><IconButton size="small" onClick={()=>openEditSection()}>D</IconButton></Tooltip>
        <Box sx={{ ml:'auto' }}>
          <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 220 }} />
        </Box>
      </Box>

      {/* Tabela */}
      <TableContainer
        component={Box}
        ref={tableContainerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={(e)=>{
          if(!((e.target as HTMLElement).closest('tr'))){ setSelectedRowId(null); }
        }}
        sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', borderRadius: 0, '&:focus': { outline: 'none' } }}>
        <Table stickyHeader size="small" sx={{
          tableLayout: 'auto',
          fontSize: '0.70rem',
          '& .MuiTableCell-root': { padding: '3px 6px' },
          '& thead .MuiTableCell-root': { fontSize: '0.78rem', fontWeight: 600 }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 70 }}>Nr</TableCell>
              <TableCell sx={{ width: 140, whiteSpace: 'nowrap' }}>Pozycje kosztorysowe</TableCell>
              <TableCell>Nazwa</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>RAZEM netto</TableCell>
              <TableCell align="center" sx={{ width: 40 }}>A</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map(row => (
              <TableRow
                key={row.id}
                tabIndex={0}
                data-rowid={`row-${row.id}`}
                selected={selectedRowId === row.id}
                onClick={() => setSelectedRowId(row.id)}
                sx={selectedRowId === row.id ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}}
              >
                {/* L.p */}
                <TableCell>{row.nr}</TableCell>

                {/* Range */}
                <TableCell>{row.range}</TableCell>

                {/* Nazwa */}
                <TableCell>{row.name}</TableCell>

                {/* RAZEM */}
                <TableCell align="right">{row.total.toLocaleString(undefined, { minimumFractionDigits: 2 })} zł</TableCell>

                {/* Akcje */}
                <TableCell align="center">
                  <IconButton size="small" onClick={e => handleMenuOpen(e, row.id)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { const row=rows.find(r=>r.id===selectedRowId); handleMenuClose(); openEditSection(row); }}>Edytuj</MenuItem>
        <MenuItem onClick={handleDeleteRow}>Usuń</MenuItem>
      </Menu>
      <AddTesSectionDialog
        open={dialogOpen}
        estimateId={estimate.id}
        onClose={() => { setDialogOpen(false); setSelectedRowId(null); setEditSection(null);} }
        onSaved={handleDialogSaved}
        section={editSection as any}
        existingNumbers={rows.map(r=>r.nr)}
      />
    </Box>
  );
};

export default TesTab; 